import { IPortfolioCompanyMetric, MetricSource } from '../models/IPortfolioCompanyMetric';
import { IPortfolioCompany } from '../models/IPortfolioCompany';
import { 
    ITearSheet, 
    IMetricProps, 
    ITearsheetCompanyInfo, 
    IFundInvestmentSummary, 
    IInvestmentProfile, 
    IInvestmentSummary, 
    IKeyOperatingStats, 
    ITearsheetCommentary
} from '../models/ITearSheet';
import recalcService from './CompanyRecalcService';
import { getLastDateInQuarter } from './DateHelper';
import CompanyRecalcService from './CompanyRecalcService';
import { getQuarter } from './DateHelper';
import { CapitalMemberType } from '@common/enums/CapitalMemberType';
import { IFundCapitalMember } from '@common/models/ICapitalMember';
import { TearsheetUnit, TearsheetValue } from '../enums/tearsheet/TearsheetState';
import { Utils } from '@common/utils/utils';
import { REMAINING_TERM_METRIC_CODE } from '@common/constants/tearsheet.constants';
import { ITearsheetPartnerFundsExcludeConfig } from '@common/models/IConfig';
import { tearsheetFundCapitalMembers, tearsheetNetGrossRatio } from '@common/constants/tearsheet.constants';

/**
 * Ratio of net to gross for each major fund. This data comes from IR for now,
 * but should be calculated from fund returns.
 */
const netGrossRatio : any = tearsheetNetGrossRatio;

let effectiveNetGrossRatio: any={};

let tearsheetName:any = '';


// Create our number formatter.
const currencyFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',

    // These options are needed to round to whole numbers if that's what you want.
    minimumFractionDigits: 0, // (this suffices for whole numbers, but will print 2500.10 as $2,500.1)
    maximumFractionDigits: 0, // (causes 2500.99 to be printed as $2,501)
});

const percentFormatter = new Intl.NumberFormat('en-US' , {
    style: 'percent',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
});

const irrFormatter = new Intl.NumberFormat('en-US', {
    style: 'percent',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
});

const ratioFormatter = new Intl.NumberFormat('en-US', {
    style: 'decimal',
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
});

// Formatting for production volumes
const productionFormatter = new Intl.NumberFormat('en-US', {
    style: 'decimal',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
});

function getRatios(effectiveNetGrossRatio:any,fund: string){
    const ratios = fund ? effectiveNetGrossRatio.filter((ratio: { name: string; })=>(ratio.name == fund)):null;
    let performanceRatios = ratios && ratios.length>0 ? ratios[0].performanceRatios : []
    const effectiveAdjNetRatios = fund && performanceRatios  && Object.keys(performanceRatios).includes(tearsheetName) ? performanceRatios[tearsheetName] : null;
    return effectiveAdjNetRatios ? effectiveAdjNetRatios : null;
}

/**
 * This class calculates the returns for a given fund member of an investment.
 */
class ReturnsCalculator {
    private netToGrossRatio: any;
    private adjustedToGrossRatio : any;
    private realizedValue: number;
    private holdingValue: number;
    private investedCapital: number;
    public grossRealizedMoic;
    public grossUnrealizedMoic;
    public grossTotalMoic;
    public netRealizedMoic
    public netUnrealizedMoic;
    public netTotalMoic;
    public adjustedRealizedMoic;
    public adjustedUnrealizedMoic;
    public adjustedTotalMoic;

    constructor(private investmentSummary: IInvestmentSummary, fund: string) {
        const fundGrossNetRatio = getRatios(effectiveNetGrossRatio,fund)
        const fundAdjGrossNetRatio = getRatios(effectiveNetGrossRatio,fund)
        this.netToGrossRatio = fundGrossNetRatio;
        this.adjustedToGrossRatio = fundAdjGrossNetRatio;
        this.realizedValue = this.investmentSummary.distributions.value as number;
        this.holdingValue = this.investmentSummary.holdingValue.value as number;
        this.investedCapital = this.investmentSummary.investedCapital.value as number;
        this.grossRealizedMoic = this.realizedValue / this.investedCapital;
        this.grossUnrealizedMoic = this.holdingValue / this.investedCapital;
        const grossTotalMoic = (this.realizedValue + this.holdingValue) / this.investedCapital;
        this.grossTotalMoic = Number.isFinite(grossTotalMoic) ? grossTotalMoic : 0;
        this.netRealizedMoic = this.netToGrossRatio && this.netToGrossRatio.netMoic ? this.grossRealizedMoic * this.netToGrossRatio.netMoic : 0;
        this.netUnrealizedMoic = this.netToGrossRatio && this.netToGrossRatio.netMoic ?  this.grossUnrealizedMoic * this.netToGrossRatio.netMoic : 0;
        this.netTotalMoic =  this.netToGrossRatio && this.netToGrossRatio.netMoic ? this.grossTotalMoic * this.netToGrossRatio.netMoic : 0;
        this.adjustedRealizedMoic = this.netToGrossRatio && this.adjustedToGrossRatio.moic ? this.grossRealizedMoic * this.adjustedToGrossRatio.moic : 0;
        this.adjustedUnrealizedMoic = this.netToGrossRatio && this.adjustedToGrossRatio.netMoic ? this.grossUnrealizedMoic * this.adjustedToGrossRatio.netMoic : 0;
        this.adjustedTotalMoic = this.netToGrossRatio && this.adjustedToGrossRatio.netMoic ? this.grossTotalMoic * this.adjustedToGrossRatio.netMoic : 0;

    }    


 
}

class TearsheetHelperService {

    public asOfDate: Date=new Date();
    public recalcService: recalcService;

    constructor(private company: IPortfolioCompany, private tearsheet: ITearSheet, private portfolioCompanyMetrics:IPortfolioCompanyMetric[],private fundCapitalMembers?:IFundCapitalMember[],private updateCapitalmembers:boolean = true) {
        if(!tearsheet) { }
        this.asOfDate = getLastDateInQuarter(tearsheet?.name || "2022-Q4");
        tearsheetName = this.tearsheet.name;
        effectiveNetGrossRatio = this.fundCapitalMembers;
        this.recalcService = CompanyRecalcService.newTrimmedCalculator(company, this.asOfDate,updateCapitalmembers);
    }
    
     static get NullString(): string {
        return "-";
    }

    static get NaNString(): string {
        return 'N/A';
    }

    static get ZeroString(): string {
        return "-";
    }

    /** 
     * Creates a new IMetricProps object with the value set to the total value
     * times the ratio.
     */
    allocatedMetric(total: IMetricProps, ratio: number): IMetricProps {
        const retVal = { ...total };
        retVal.value = (total.value === null || total.value === undefined) ? total.value : <number>total.value * ratio;
        return retVal;
    }

    /**
     * format a metric for display.
     * @param metric the metric to format
     * @param millions if true, divide by 1 million and add "MM" to the end.
     */
    public format(metric: IMetricProps, millions: boolean = false): string {
    //Temporary fix for QGEN and FPR irr value     
        if (metric.value === '>100%') {
            return '>100%';
        }
        if (!metric || (!metric.value && metric.value !== 0)) return metric?.unit === TearsheetUnit.irr? TearsheetHelperService.NaNString : TearsheetHelperService.NullString;
        if (metric.unit === TearsheetUnit.date) {
            return new Date(metric.value as Date).toLocaleDateString("en-US", { timeZone: 'UTC' });
        }
        else if (metric.unit === TearsheetUnit.shortDate) {
            const date = new Date(metric.value as Date);
            const [month, year] = new Intl.DateTimeFormat('en-US', { month: 'short', year: 'numeric' })
                .format(date)
                .split(' ');
            return `${month}. ${year}`;
        }
        else if (metric.unit === TearsheetUnit.percent) {
            if (metric.value === '-' || metric.value === null || metric.value === '') return (TearsheetHelperService.NullString);
            if (Number.isNaN(metric.value)) return (TearsheetHelperService.NullString);
            const value = metric.value as number;
            if (typeof metric.metricCode !== 'undefined' && value === 0)  return '0';
            return percentFormatter.format(value as number);
            
        }
        else if (metric.unit === TearsheetUnit.irr) {
            if(metric.value === TearsheetValue.COST) return (metric.value)
            if (Number.isNaN(metric.value)) return (TearsheetHelperService.NaNString);
            if (metric.value as number === 0) return '0';
            if (metric.value as number < 0) return (TearsheetHelperService.NaNString);
            return irrFormatter.format(metric.value as number); 
        }
        else if (metric.unit === TearsheetUnit.ratio) {
            if (Number.isNaN(metric.value)) return (TearsheetHelperService.NullString);
            if (metric.value == 0) return (TearsheetHelperService.ZeroString);
            return ratioFormatter.format(metric.value as number)+"x";
        }
        else if (metric.unit === TearsheetUnit.USD) {
            if (Number.isNaN(metric.value)) return (TearsheetHelperService.NullString);
            if (typeof metric.metricCode !== 'undefined' && metric.value === 0) return '0';
            if (metric.value == 0) return (TearsheetHelperService.ZeroString);
            if (millions) {
                return (metric.value as number < 0) ? "(" +currencyFormatter.format(Math.abs(metric.value as number) / 1000000.0) + ") MM" : currencyFormatter.format(metric.value as number / 1000000.0) + " MM" ;
            }
            else {
                return (metric.value as number < 0) ? "("+ currencyFormatter.format(Math.abs(metric.value as number)) + ")":currencyFormatter.format(metric.value as number);
            }
        }
        else if (metric.unit === TearsheetUnit.Boepd || metric.unit === TearsheetUnit.Mboe || metric.unit === TearsheetUnit.Bcfe || metric.unit === TearsheetUnit.Mcfepd || metric.unit === TearsheetUnit.Mmbtupd || metric.unit === TearsheetUnit.Mcfd) {
            if (Number.isNaN(metric.value)) return (TearsheetHelperService.NullString);
            if (metric.value as Number == 0) return TearsheetHelperService.ZeroString;
            return productionFormatter.format(metric.value as number) + " " + metric.unit;
        }
        else if (metric.unit === TearsheetUnit.unitless){
            if (metric.metricCode === REMAINING_TERM_METRIC_CODE) {
                return Utils.getRemainingTermDisplay(metric.value);
            }
            return metric.value as string;
        }
        return (metric.value || TearsheetHelperService.NullString).toString();
    }

    /**
     * return a list of fund members as a simple string array.
     */
       public get allFunds(): string[] {
        const fis = this.tearsheet.fundInvestmentSummary;
        return fis.map(x => x.fund?.value as string);
    }

    /**
     * return a list of fund members as a simple string array. We only return
     * main fund and coinvest, for any family.
     */
    public get funds(): string[] {
        // TODO, make this a system var, or a property of fundcaptialmembers.
        const tearsheetFunds: string[] = tearsheetFundCapitalMembers;

        const fundCapitalMembers = [...this.tearsheet.fundInvestmentSummary.map(x => x.fund.value as string)];
        const result = fundCapitalMembers.filter(fund => tearsheetFunds.includes(fund));

        return result;
    }

    /**
     * return a list of fund members as a simple string array. We only return
     * main fund and coinvest, for any family.
     */
    public get fundsForAggregatedTS(): string[] {
        // TODO, make this a system var, or a property of fundcaptialmembers.
        const tearsheetFunds: string[] = tearsheetFundCapitalMembers;

        const fundCapitalMembers = [...this.tearsheet.fundInvestmentSummary.map(x => x.fund.value as string)];
        const result = tearsheetFunds.filter(fund => fund.startsWith(fundCapitalMembers[0]));

        return result;
    }

    /**
     * 
     * @returns a map of the ratios of the fund names that are associated with this PC.
     */
    fundNetGrossRatios():any {
        const fundNames = this.company.capitalMembers.map(c=>c.name);
        const fundNetGrossRatioMapping:any[] = [];
        fundNames.forEach(fundName =>{
            if(netGrossRatio[fundName]){
                fundNetGrossRatioMapping.push(netGrossRatio[fundName])
            }
        })
        return fundNetGrossRatioMapping;
    }

    /**
     * 
     * @returns a map of the ratios of the fund names that are associated with this PC.
     */

    fundCapmemberRatio(effectiveNetGrossRatio: any) {
        const fundNames = this.company.capitalMembers.map(c=>{if(c.memberType == CapitalMemberType.MainFund)return c.name} );
        const fundNetGrossRatioMapping:any[] = [];
        fundNames.forEach(fundName =>{
            if(fundName){
                const ratio = getRatios(effectiveNetGrossRatio,fundName)
                if(ratio){
                    fundNetGrossRatioMapping.push(getRatios(effectiveNetGrossRatio,fundName))
                }
            }
        })
        return fundNetGrossRatioMapping;
    }

    /**
     * 
     * @param selectedDate The target date for the key operating stats
     * @returns A list of it plus the previous 3 quarters as strings. Used in
     *          the front-end to populate the dropdown.
     */
    public getKeyOperatingQuarters(selectedDate: Date){
        const today = new Date(selectedDate);
        const quarter = Math.floor(today.getMonth() / 3); 
        let quarterArr = [];
        // to calculate last three quarters date;
        for(let i=0; i<=3; i++){
            let startDate = new Date(today.getFullYear(), quarter * 3 - (i*3), 1);
            let endDate = new Date(startDate.getFullYear(), startDate.getMonth() + 3, 0);
            quarterArr.push({'name' : getQuarter(endDate)})
        }

        return quarterArr
    }

    /**
     * 
     * @param stats The metric code for the metric we want to find.
     * @returns The value of the metric for the current tearsheet, using a 
     *          priority order for the sources.
     */
    getMetricsByCode(stats:string) { 
        // Prioritize the sources for the key operating stats. We will use the first one we find.
        const source_priority = [MetricSource.QUILT, MetricSource.Q360, MetricSource.DEALTEAM];

        function sourcePriorty(metric1: MetricSource, metric2: MetricSource) {
            return source_priority.indexOf(metric1) - source_priority.indexOf(metric2);
        }

        const metrics = this.portfolioCompanyMetrics
            .filter(x=>(x.metricCode == stats))
            .sort((a, b) => sourcePriorty(a.source, b.source));

        return metrics[0];
    };
    loadCommentary(): ITearsheetCommentary {
        return {
            companyOverview: this.tearsheet.commentary?.companyOverview || "-0-",
            investmentThesis: this.tearsheet.commentary?.investmentThesis || "-0-",
            significantDevelopments: this.tearsheet.commentary?.significantDevelopments || "-0-",
        };
    }

    public loadGeneralInfo(): ITearsheetCompanyInfo {
        return {
            name: this.company.legalName || "-0-",
            logo: this.company.image || "missing.png",
            website: this.company.website || "-0-",
            funds: this.company.fundFamily,
            tearSheetDate: this.asOfDate,
            operatingMetricsDate: this.tearsheet.generalInfo.operatingMetricsDate || this.asOfDate,
            quantumDirectors: this.company.team?.boardMembers || [],
        };
    }

    public loadInvestmentProfile(reloadMetrics=false, metrics?: any): Promise<IInvestmentProfile> {
        const investmentProfile:any = {
            investmentDate: { displayName: this.getInvestmentDateName(), value: this.tearsheet?.investmentProfile?.investmentDate?.value ?? this.recalcService.investmentDate(), unit: "date" },
            industry: { displayName: "Industry", value: this.company.industry || "", unit: "unitless" },
            geography: { displayName: "Geography", value: Utils.standardizeGeography(this.company.geography), unit: "unitless" },
            basins: { displayName: this.company.basins?.length > 1 ? "Basins" : "Basin", value: this.company.basins?.join("\n") || "", unit: "unitless" },
            investmentType: { displayName: "Investment Type", value: this.company.investmentType || "", unit: "unitless" },
            ownership: { displayName: "Ownership", value: this.tearsheet?.investmentProfile?.ownership?.value || TearsheetHelperService.NullString, unit: "percent" },
            investmentStatus: { displayName: "Investment Status", value: this.recalcService.investmentStatus || "", unit: "unitless" },
        } ;

        /**
         * This is a little backwards, but in order to keep basins in the correct
         * order, we must first add it, and the remove it if it is null
         */
        if (this.company.basins?.length < 1) {
            delete investmentProfile.basins;
        }
        // Dynamic metrics
        const dynamicMetrics: any = { ...investmentProfile };
        if (metrics) {
            for (const stats of Object.keys(metrics)) {
                dynamicMetrics[stats] = reloadMetrics
                    ? this.getMetricsByCode(stats)
                    : this.tearsheet?.investmentProfile?.[stats] ;
            }
        }
        return dynamicMetrics ;
    }
    public getInvestmentDateName(){
        const value = this.tearsheet?.investmentProfile?.investmentDate?.value;
        const recalculated = this.recalcService.investmentDate();

        const date1 = value ? new Date(value) : null;
        const date2 = recalculated ? new Date(recalculated) : null;
        if (!date1 || isNaN(date1.getTime()) || !date2 || isNaN(date2.getTime())) {
            return this.tearsheet?.investmentProfile?.investmentDate?.displayName ?? "Investment Date";
        }
        const formattedDate1 = date1.toISOString().split('T')[0];
        const formattedDate2 = date2.toISOString().split('T')[0];
        
        if(formattedDate1 === formattedDate2){
            return "Investment Date";
        }
        else{
          return this.tearsheet?.investmentProfile?.investmentDate?.displayName ?? "Investment Date"  ;
        }
    }

    public loadInvestmentSummary(): IInvestmentSummary {
        return {
            totalCommitment: { displayName: "Commitment", value: this.recalcService.totalCommitments(), unit: TearsheetUnit.USD },
            investedCapital: { displayName: "Contributions", value: this.recalcService.totalContributions(), unit: TearsheetUnit.USD },
            distributions: { displayName: "Distributions", value: this.recalcService.hasDistributions() ? this.recalcService.totalDistributions() : null, unit: TearsheetUnit.USD },
            holdingValue: { displayName: "Holding Value", value: this.recalcService.holdingValue(), unit: TearsheetUnit.USD },
            enterpriseValue: { displayName: "Enterprise Value", value: this.recalcService.enterpriseValue(), unit: TearsheetUnit.USD },
            totalValue: { displayName: "Total Value", value: this.recalcService.totalDistributions() + this.recalcService.holdingValue(), unit: TearsheetUnit.USD },
            
            grossTotalMoic: { displayName: "Gross MOIC", value: this.recalcService.moic(), unit: TearsheetUnit.ratio },
            netTotalMoic: { displayName: "Total MOIC", value: this.netMoicForFund(this.recalcService.moic(), ""), unit: TearsheetUnit.ratio },
            adjustedTotalMoic : { displayName: "Adjusted MOIC", value: this.adjustedMoicForFund(this.recalcService.moic(), ""), unit: TearsheetUnit.ratio },
            
            grossIrr: { displayName: "Gross IRR", value: this.grossIRRValue(this.recalcService.moic()), unit: TearsheetUnit.irr },
            netIrr:   { displayName: "IRR", value: this.netIrrForFund(this.recalcService.xirr(this.asOfDate),""), unit: TearsheetUnit.irr },
            adjustedIrr:   { displayName: "IRR", value: this.adjustedIrrForFund(this.recalcService.xirr(this.asOfDate),""), unit: TearsheetUnit.irr },
        };
    }

    public loadFundInvestmentSummary(): IFundInvestmentSummary[] {

        // return value
        const fundInvestmentSummary: IFundInvestmentSummary[] = [];

        // This needs to be an array of any capital members that are main funds.
        for (const member of this.company.capitalMembers) {
            
            // For now, any member that is a fund member is included.
            // TODO understand how this works for QCS fund of one members.
            if (member.memberType == CapitalMemberType.MainFund || member.memberType == CapitalMemberType.Coinvestment) {

                // This is the sharing ratio for this member, may need to move these calcs to the
                // recalcService.
                const ratio = this.recalcService.fundRatio(member.name,this.asOfDate);

                // For splitLogicEnabled companies, fundRatio reflects the funded-to-date share
                // (actual capital call amounts), which diverges from the commitment ratio when
                // funds deploy capital at different rates than their committed shares. totalCommitment
                // is a contractual amount, not a cash-flow metric, so it must be read directly from
                // the commitment record rather than derived from fundRatio.
                const totalCommitmentMetric = this.company?.splitLogicEnabled
                    ? { ...this.tearsheet.investmentSummary.totalCommitment, value: this.recalcService.fundCommitmentAmount(member.name ?? '') }
                    : this.allocatedMetric(this.tearsheet.investmentSummary.totalCommitment!, ratio);

                // First populate with the values are ratios of the value for the whole company.
                let fundSummary : IFundInvestmentSummary = {
                    fund: { displayName: "Fund", value: member.name, unit: TearsheetUnit.unitless },
                    sharingRatio: { displayName: "Sharing Ratio", value: ratio, unit: TearsheetUnit.percent },
                    totalCommitment: totalCommitmentMetric,
                    investedCapital: this.allocatedMetric(this.tearsheet.investmentSummary.investedCapital, ratio),
                    distributions: this.allocatedMetric(this.tearsheet.investmentSummary.distributions, ratio),
                    holdingValue: this.allocatedMetric(this.tearsheet.investmentSummary.holdingValue, ratio),
                    enterpriseValue: this.allocatedMetric(this.tearsheet.investmentSummary.enterpriseValue, ratio),
                    totalValue: this.allocatedMetric(this.tearsheet.investmentSummary.totalValue, ratio)
                }


                // Now calculate the MOICs and IRRs for this fund.
                const calc = new ReturnsCalculator(fundSummary, member.name);
                fundSummary.grossRealizedMoic = { displayName: "Realized MOIC", value: calc.grossRealizedMoic, unit: TearsheetUnit.ratio },
                fundSummary.netRealizedMoic = { displayName: "Realized MOIC", value: calc.netRealizedMoic, unit: TearsheetUnit.ratio },
                fundSummary.grossUnrealizedMoic = { displayName: "Unrealized MOIC", value: calc.grossUnrealizedMoic, unit: TearsheetUnit.ratio },
                fundSummary.netUnrealizedMoic = { displayName: "Unrealized MOIC", value: calc.netUnrealizedMoic, unit: TearsheetUnit.ratio },
                fundSummary.grossTotalMoic = { displayName: "Total MOIC", value: calc.grossTotalMoic, unit: TearsheetUnit.ratio },
                fundSummary.netTotalMoic = { displayName: "Total MOIC", value: calc.netTotalMoic, unit: TearsheetUnit.ratio },
                fundSummary.grossIrr = { displayName: "IRR", value: this.grossIRRValue(calc.grossTotalMoic) , unit: TearsheetUnit.irr },
                fundSummary.netIrr =   { displayName: "IRR", value: this.netIrrForFund(this.recalcService.xirr(this.asOfDate), member.name), unit: TearsheetUnit.irr },
                fundSummary.adjustedIrr =   { displayName: "IRR", value: this.adjustedIrrForFund(this.recalcService.xirr(this.asOfDate), member.name), unit: TearsheetUnit.irr },
                fundSummary.adjustedRealizedMoic = { displayName: "Realized MOIC (Adjusted)", value: calc.adjustedRealizedMoic, unit: TearsheetUnit.ratio}
                fundSummary.adjustedUnrealizedMoic = { displayName: "Unrealized MOIC (Adjusted)", value: calc.adjustedUnrealizedMoic, unit: TearsheetUnit.ratio}
                fundSummary.adjustedTotalMoic = { displayName: "Adjusted Total Moic (Adjusted)", value: calc.adjustedTotalMoic, unit: TearsheetUnit.ratio}

                 if(this.company?.splitLogicEnabled){
                   fundSummary.investedCapital =   { displayName: "Contributions", value: this.recalcService.fundWiseContributionByDate(this.asOfDate, member.name), unit: TearsheetUnit.USD };
                   fundSummary.distributions = { displayName: "Distributions", value: this.recalcService.hasDistributions() ? this.recalcService.fundWiseDistributionByDate(this.asOfDate, member.name) : null, unit: TearsheetUnit.USD };
                   fundSummary.grossIrr =  { displayName: "IRR", value: this.grossIRRValue(calc.grossTotalMoic, member.name) , unit: TearsheetUnit.irr };
                   fundSummary.netIrr =   { displayName: "IRR", value: this.netIrrForFund(this.recalcService.xirr(this.asOfDate, member.name), member.name), unit: TearsheetUnit.irr };
                   fundSummary.adjustedIrr =   { displayName: "IRR", value: this.adjustedIrrForFund(this.recalcService.xirr(this.asOfDate, member.name), member.name), unit: TearsheetUnit.irr };
                }
                fundInvestmentSummary.push(fundSummary);
            }
            
        }

        return fundInvestmentSummary;
    }
    grossIRRValue(moic: any, memberName?: string): any{
        return Utils.isCheckMoicValue(moic) ? TearsheetValue.COST : this.company?.splitLogicEnabled? this.recalcService.xirr(this.asOfDate, memberName) : this.recalcService.xirr(this.asOfDate);
    }

    loadKeyOperatingStats(metrics?: any): Promise<IKeyOperatingStats> {
        let keyOperatingStats:any = {}
        if (metrics) {
            for (const [key, metricDef] of Object.entries(metrics)) {
                    const metricValue = this.getMetricsByCode((metricDef as any).metricCode);
                    keyOperatingStats[key] = metricValue;
            }
        }
        return keyOperatingStats ;
    }

    /**
     * 
     * @param netGrossRatio a list of the netGrossRatio values, filtered for funds that are associated with this PC. This
     *                      ratio will be used as the overall netGrossRatio for the whole company.
     * @returns the lowest netGrossRatio of MOIC among this list
     */
    lowestMoic(netGrossRatio:any) {
        return netGrossRatio.sort(function(a: { netMoic: number; },b: { netMoic: number; }){return a.netMoic - b.netMoic})[0];
    }

    lowestAdjustedMoic(netGrossRatio:any) {
        return netGrossRatio.sort(function(a: { adjustedMoic: number; },b: { adjustedMoic: number; }){return a.adjustedMoic - b.adjustedMoic})[0];
    }

    /**
     * 
     * @param netGrossRatio a list of the netGrossRatio values, filtered for funds that are associated with this PC. This
     *                      ratio will be used as the overall netGrossRatio for the whole company.
     * @returns the lowest netGrossRatio of IRR among this list
     */
    lowestAdjustedIrr(netGrossRatio:any){
        //Get the lowest netGrossRatio irr value
        return netGrossRatio.sort(function(a: { adjustedIrr: number; },b: { adjustedIrr: number; }){return a.adjustedIrr - b.adjustedIrr})[0];
    }

    lowestIrr(netGrossRatio:any){
        //Get the lowest netGrossRatio irr value
        return netGrossRatio.sort(function(a: { netIrr: number; },b: { netIrr: number; }){return a.netIrr - b.netIrr})[0];
    }

    netIrrForFund(grossIrr: number, fund: string): number {
        //net gross ratio from fundcapitalmembers
        let fundCapitalMembersRatio = getRatios(effectiveNetGrossRatio,fund);
        if(!fund){
            const fundNetGrossRatio =  this.fundCapmemberRatio(effectiveNetGrossRatio).length>0? this.fundCapmemberRatio(effectiveNetGrossRatio) : {};
            fundCapitalMembersRatio = fundNetGrossRatio && Object.keys(fundNetGrossRatio).length > 0 ? this.lowestIrr(fundNetGrossRatio) : {}
        }
        const ratio = fundCapitalMembersRatio ; 
        return ratio && Object.keys(ratio).length > 0 ? grossIrr * ratio.netIrr : 0;
    }

    netMoicForFund(grossMoic: number, fund: string): number {
        //net gross ratio from fundcapitalmembers
        let fundCapitalMembersRatio = getRatios(effectiveNetGrossRatio,fund);
        if(!fund){
            const fundNetGrossRatio =  this.fundCapmemberRatio(effectiveNetGrossRatio).length>0? this.fundCapmemberRatio(effectiveNetGrossRatio) : {};
            fundCapitalMembersRatio = fundNetGrossRatio && Object.keys(fundNetGrossRatio).length > 0 ? this.lowestMoic(fundNetGrossRatio) : {}
        }
        const ratio = fundCapitalMembersRatio;
        return ratio && Object.keys(ratio).length > 0 ? grossMoic * ratio.netMoic : 0;
    }


    adjustedMoicForFund(grossMoic: number, fund: string): number {
        //net gross ratio from fundcapitalmembers
        let fundCapitalMembersRatio = getRatios(effectiveNetGrossRatio,fund);
        if(!fund){
            const fundNetGrossRatio =  this.fundCapmemberRatio(effectiveNetGrossRatio).length>0? this.fundCapmemberRatio(effectiveNetGrossRatio) : {};
            fundCapitalMembersRatio = fundNetGrossRatio && Object.keys(fundNetGrossRatio).length > 0 ? this.lowestAdjustedMoic(fundNetGrossRatio) : {}
        }
        const ratio = fundCapitalMembersRatio ;
        return ratio && Object.keys(ratio).length > 0 ? grossMoic * ratio.adjustedGrossMoic : 0;
    }

    adjustedIrrForFund(grossIrr: number, fund: string): number {
         //net gross ratio from fundcapitalmembers
        let fundCapitalMembersRatio = getRatios(effectiveNetGrossRatio,fund);
        if(!fund){
            const fundNetGrossRatio =  this.fundCapmemberRatio(effectiveNetGrossRatio).length>0? this.fundCapmemberRatio(effectiveNetGrossRatio) : {};
            fundCapitalMembersRatio = fundNetGrossRatio && Object.keys(fundNetGrossRatio).length > 0 ? this.lowestAdjustedIrr(fundNetGrossRatio) : {}
        }
        const ratio = fundCapitalMembersRatio;      
        return ratio && Object.keys(ratio).length > 0 ? grossIrr * ratio.netIrr : 0;
    }

  

    public static newTearsheet(mnemonic: string, name: string): ITearSheet {
        return {
            mnemonic: mnemonic,
            name: name,
            locked: false,
            noOperatingMetrics: false,
            generalInfo: {
                tearSheetDate: getLastDateInQuarter(name),
                operatingMetricsDate: getLastDateInQuarter(name)
            },
            investmentSummary: {},
            investmentProfile: {},
            fundInvestmentSummary: [],
            keyOperatingStats: {},
            commentary: {},
        };
    }

    /**
     * 
     * @returns the title for the key operating stats section of the tearsheet.
     */
    public operatingStatsTitle(): string {
        const theDate = this.tearsheet.generalInfo.operatingMetricsDate;
        return 'Key Metrics (As of ' + theDate.toLocaleDateString('en-US', {timeZone: 'UTC'}) + ')';
    }

    /**
     * 
     * @param fund this particular capital member fund name
     * @returns the output file name for this tearsheet.
     */
    public outputFileName(fund: string): string {
        return `${this.company.mnemonic}_${this.tearsheet.name}_${fund}_tearsheet.pdf`;
    }

    public outputHtmlName(fund: string): string {
        return `${this.company.mnemonic}_${this.tearsheet.name}_${fund}_tearsheet.html`;
    }

    public outputAggregatedFileName(fund: string): string {
        return `${this.company.mnemonic}_${this.tearsheet.name}_${fund}_Aggregated_tearsheet.pdf`;
    }

    public setMetrics(metrics: IPortfolioCompanyMetric[]){
        this.portfolioCompanyMetrics = metrics
    }

    /**
     * return the template name for this tearsheet.
     * 
     * This currently returns "default" but could be extended to return a 
     * different template. For now, we only have one template and it is
     * specialized only by the list of metrics.
     */
    public get templateName(): string {
        return "default";
    }

    public fundsExcludeForInvestmentSummary(fundsExclude: ITearsheetPartnerFundsExcludeConfig, tearsheetDataForAggregatedTS?:ITearSheet): ITearSheet {
        // Copy investmentSummary data
        this.tearsheet.investmentSummaryWithExcludePartner = JSON.parse(JSON.stringify(this.tearsheet.investmentSummary));
        this.tearsheet.investmentSummaryWithExcludePartnerForAggregatedPDF = tearsheetDataForAggregatedTS?.investmentSummaryWithExcludePartnerForAggregatedPDF
            ? JSON.parse(JSON.stringify(tearsheetDataForAggregatedTS.investmentSummaryWithExcludePartnerForAggregatedPDF))
            : tearsheetDataForAggregatedTS?.investmentSummary
                ? JSON.parse(JSON.stringify(tearsheetDataForAggregatedTS.investmentSummary))
                : undefined;
        // Exclude Funds
        if (fundsExclude) {
            const filteredList = (this.tearsheet?.fundInvestmentSummary ?? []).filter(fis =>
                !fundsExclude.funds.map(f => f.toLowerCase()).includes(String(fis.fund.value).toLowerCase())
            );

            // Calculate values
            let totalCommitment = 0;
            let distributions = 0;
            let hasAnyDistributions = false;
            let investedCapital = 0;
            let holdingValue = 0;
            for (const fl of filteredList) {
                totalCommitment += Number(fl.totalCommitment?.value ?? 0);
                if (fl.distributions?.value !== null && fl.distributions?.value !== undefined) {
                    hasAnyDistributions = true;
                    distributions += Number(fl.distributions.value);
                }
                investedCapital += Number(fl.investedCapital?.value ?? 0);
                holdingValue += Number(fl.holdingValue?.value ?? 0);
            }

            // Set investmentSummaryWithExcludePartner value
            this.tearsheet!.investmentSummaryWithExcludePartner!.totalCommitment!.value = Number(totalCommitment);
            this.tearsheet!.investmentSummaryWithExcludePartner!.distributions!.value = (hasAnyDistributions && distributions > 0) ? Number(distributions) : null;
            this.tearsheet!.investmentSummaryWithExcludePartner!.investedCapital!.value = Number(investedCapital);
            this.tearsheet!.investmentSummaryWithExcludePartner!.holdingValue!.value = Number(holdingValue);

        }

        if (fundsExclude && tearsheetDataForAggregatedTS !== null && this.tearsheet.investmentSummaryWithExcludePartnerForAggregatedPDF) {
            const filteredList = (tearsheetDataForAggregatedTS?.fundInvestmentSummary ?? []).filter(fis => 
                !fundsExclude.funds.map(f => f.toLowerCase()).includes(String(fis.fund.value).toLowerCase())
            );

            // Calculate values
            let totalCommitment = 0;
            let distributions = 0;
            let hasAnyDistributions = false;
            let investedCapital = 0;
            let holdingValue = 0;
            for (const fl of filteredList) {
                totalCommitment += Number(fl.totalCommitment?.value ?? 0);
                if (fl.distributions?.value !== null && fl.distributions?.value !== undefined) {
                    hasAnyDistributions = true;
                    distributions += Number(fl.distributions.value);
                }
                investedCapital += Number(fl.investedCapital?.value ?? 0);
                holdingValue += Number(fl.holdingValue?.value ?? 0);
            }

            // Set investmentSummaryWithExcludePartnerForAggregatedPDF value
            this.tearsheet!.investmentSummaryWithExcludePartnerForAggregatedPDF!.totalCommitment!.value = Number(totalCommitment);
            this.tearsheet!.investmentSummaryWithExcludePartnerForAggregatedPDF!.distributions!.value = (hasAnyDistributions && distributions > 0) ? Number(distributions) : null;
            this.tearsheet!.investmentSummaryWithExcludePartnerForAggregatedPDF!.investedCapital!.value = Number(investedCapital);
            this.tearsheet!.investmentSummaryWithExcludePartnerForAggregatedPDF!.holdingValue!.value = Number(holdingValue);
        }

        return this.tearsheet;
    }
}

export default TearsheetHelperService;